import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DoCheck, ElementRef, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { IParkStep, PARK_STEPS } from '../park-steps/park-steps.component';

export interface IParkFeatured {
  hotel: any;
  name: string;
  image: string;
  price: number;      // total de la estancia de la primera habitación (misma cifra que la tarjeta del listado)
  currency: string;
  tier: string;       // Económico | Moderado | Lujo ('' si no se conoce)
}

// Categoría del resort. Es contenido fijo de la vista (Octopus no la envía): fragmento del nombre en minúsculas → categoría.
const RESORT_TIERS: Array<[string, string]> = [
  // Walt Disney World
  ['pop century', 'Económico'], ['all-star', 'Económico'], ['all star', 'Económico'], ['art of animation', 'Económico'],
  ['port orleans', 'Moderado'], ['caribbean beach', 'Moderado'], ['coronado springs', 'Moderado'],
  ['contemporary', 'Lujo'], ['polynesian', 'Lujo'], ['grand floridian', 'Lujo'], ['wilderness lodge', 'Lujo'],
  ['yacht club', 'Lujo'], ['beach club', 'Lujo'], ['boardwalk', 'Lujo'], ['animal kingdom lodge', 'Lujo'],
  ['riviera', 'Lujo'], ['saratoga springs', 'Lujo'], ['old key west', 'Lujo'], ['bay lake tower', 'Lujo'],
  // Universal Orlando
  ['cabana bay', 'Económico'], ['endless summer', 'Económico'],
  ['aventura', 'Moderado'], ['sapphire falls', 'Moderado'],
  ['portofino', 'Lujo'], ['hard rock', 'Lujo'], ['royal pacific', 'Lujo'],
];

const FEATURED_LIMIT = 8;

/**
 * Cabecera del listado de Parques: los pasos de la compra (el listado es el paso 1, Hotel) y un carrusel con los
 * primeros hoteles del resultado, con su foto de portada, el precio "Desde" y la categoría del resort.
 * Al elegir un hotel se avisa al listado para llevar a su tarjeta.
 */
@Component({
  selector: 'usb-park-results-header',
  templateUrl: './park-results-header.component.html',
  styleUrls: ['./park-results-header.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ParkResultsHeaderComponent implements DoCheck {
  @Input() hotels: any[] = [];
  @Input() currencies: any[];
  @Input() currencyAgency: any;

  @Output() select = new EventEmitter<any>();

  @ViewChild('track') track: ElementRef<HTMLElement>;

  readonly steps: IParkStep[] = PARK_STEPS;
  featured: IParkFeatured[] = [];
  canPrev = false;
  canNext = false;

  private signature = '';

  constructor(private cdr: ChangeDetectorRef) {}

  // El listado se reordena o filtra sin cambiar de referencia: se compara el orden de los primeros hoteles.
  ngDoCheck(): void {
    const first = (this.hotels || []).slice(0, FEATURED_LIMIT);
    const signature = first.map(h => String(h?.productId ?? h?.id ?? h?.title ?? '')).join('|');
    if (signature === this.signature) {
      return;
    }
    this.signature = signature;
    this.featured = first.map(h => this.toFeatured(h));
    this.cdr.markForCheck();
    setTimeout(() => this.updateArrows());
  }

  move(direction: number): void {
    const el = this.track?.nativeElement;
    if (el) {
      el.scrollBy({ left: direction * el.clientWidth, behavior: 'smooth' });
    }
  }

  updateArrows(): void {
    const el = this.track?.nativeElement;
    this.canPrev = !!el && el.scrollLeft > 4;
    this.canNext = !!el && el.scrollLeft + el.clientWidth < el.scrollWidth - 4;
    this.cdr.markForCheck();
  }

  trackByHotel(index: number, featured: IParkFeatured): string {
    return String(featured.hotel?.productId ?? featured.hotel?.id ?? index);
  }

  private toFeatured(hotel: any): IParkFeatured {
    const info = hotel?.additionalInfo || {};
    const images: any[] = info.images || [];
    const cover = images.find(i => i?.is_cover === 1) || images[0];
    const room = info.rooms?.[0]?.room;
    return {
      hotel,
      name: hotel?.title || '',
      image: cover?.image || cover?.thumbnail || '/assets/img/app/hotel_default.png',
      price: Number(room?.optionNightsNetTotalProf || 0) + Number(room?.pricing?.taxesExcluded || 0),
      currency: room?.pricing?.currency || 'USD',
      tier: this.tierOf(hotel?.title),
    };
  }

  private tierOf(title: string): string {
    const name = String(title || '').toLowerCase();
    const match = RESORT_TIERS.find(([key]) => name.includes(key));
    return match ? match[1] : '';
  }
}
